/**
 * Shared view-model types for the Project Intelligence widgets, plus the
 * score -> health band mapping they colour themselves by.
 *
 * The widgets are deliberately unaware of the KPI engine: `intelligence.ts`
 * turns KpiValueDto/KpiHistoryDto/ActionAlertDto into these shapes.
 */
import { status } from './reportTheme';

export interface KpiDatum {
  key: string;
  value: string;
  delta: string;
  higherIsBetter: boolean;
  direction: 'up' | 'down' | 'flat';
}

export interface CategoryDatum {
  label: string;
  value: number;
}

/** One category measured twice - plan against actual. */
export interface ComparisonDatum {
  label: string;
  planned: number;
  actual?: number;
}

export interface StatusDatum {
  status: string;
  value: number;
  color: string;
}

export type ProjectHealthKey = 'onTrack' | 'watch' | 'atRisk' | 'critical';

/**
 * Same thresholds as the backend's ph.project_health_status (spec §12), kept
 * here so a widget can colour a bare score. Where the engine already supplies
 * the status, prefer it over re-deriving - see `healthKeyFromStatus`.
 */
export function getProjectHealthStatus(score: number): { key: ProjectHealthKey; color: string } {
  if (score >= 80) return { key: 'onTrack', color: status.good };
  if (score >= 65) return { key: 'watch', color: status.warning };
  if (score >= 45) return { key: 'atRisk', color: status.serious };
  return { key: 'critical', color: status.critical };
}
